import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { Button, Spacing, Text } from '@reservamos/elements';
import ModalWithTheme from 'components/ModalWithTheme';
import BirthDateField from 'components/BirthDateField';
import { fieldRenderer } from 'utils/formRenderers';
import { createValidator, cpf, email, required, date } from 'utils/formValidations';
import submitSeatRequest from '@/actions/seatRequest';

const FORM = 'negativaForm';

const validate = createValidator({
  name: [required],
  taxpayerId: [required, cpf],
  email: [required, email],
  birthDate: [required, date],
});

/**
 * DenialFormModal component.
 * Render the form modal that captures beneficiary data and submits the
 * "Negativa de Concessão de Benefício" request.
 *
 * @param {object} props - Redux-form-injected props plus modal handlers.
 * @param {Function} props.handleSubmit - Redux-form submit handler.
 * @param {Function} props.onClose - Closes the modal (X icon and Fechar button).
 * @param {boolean} props.invalid - True when the form has validation errors.
 * @param {boolean} props.submitting - True while the form is submitting.
 * @returns {JSX.Element} The rendered modal.
 */
const DenialFormModal = ({ handleSubmit, onClose, invalid, submitting }) => {
  const { t } = useTranslation('search');

  return (
    <ModalWithTheme
      title={t('negativa.form.title')}
      onCloseModal={onClose}
      closeOnOverlayClick={!submitting}
      rounded="M"
      responsiveSize="S"
    >
      <form onSubmit={handleSubmit}>
        <Spacing vertical size="M">
          <Text>{t('negativa.form.subtitle')}</Text>
          <Field
            name="name"
            id="negativa-name"
            type="text"
            label={t('negativa.form.name_label')}
            placeholder={t('negativa.form.name')}
            component={fieldRenderer}
          />
          <Field
            name="taxpayerId"
            id="negativa-taxpayer-id"
            type="text"
            inputMode="numeric"
            maxLength={11}
            label={t('negativa.form.taxpayer_id_label')}
            placeholder={t('negativa.form.taxpayer_id')}
            component={fieldRenderer}
          />
          <Field
            name="email"
            id="negativa-email"
            type="email"
            label={t('negativa.form.email_label')}
            placeholder={t('negativa.form.email')}
            component={fieldRenderer}
          />
          <Field name="birthDate" component={BirthDateField} passengerType="adult" />
          <Spacing vertical size="S">
            <Button
              variant="primary"
              text={t('negativa.form.submit')}
              buttonType="submit"
              isDisabled={invalid || submitting}
              isLoading={submitting}
            />
            <Button
              variant="noFill"
              text={t('negativa.form.close')}
              buttonType="button"
              onClick={onClose}
              isDisabled={submitting}
            />
          </Spacing>
        </Spacing>
      </form>
    </ModalWithTheme>
  );
};

DenialFormModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  invalid: PropTypes.bool,
  submitting: PropTypes.bool,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onSubmit: async (values) => {
    await dispatch(submitSeatRequest(values, ownProps.tripContext));
    ownProps.onSuccess?.();
  },
});

export default compose(
  connect(null, mapDispatchToProps),
  reduxForm({
    form: FORM,
    destroyOnUnmount: true,
    validate,
    onChange: (values, dispatch) => {
      if (values.taxpayerId) {
        const digitsOnly = values.taxpayerId.replace(/\D/g, '');
        if (digitsOnly !== values.taxpayerId) {
          dispatch(change(FORM, 'taxpayerId', digitsOnly));
        }
      }
    },
  }),
)(DenialFormModal);
